import {
  documentValidator,
  strictDocumentValidator,
  validateAgainstSchema,
  validateJson
} from "./chunk-MKBB5XJN.js";
import {
  ComponentDefinitionSchema
} from "./chunk-WJYTQIJN.js";
import {
  ReportPropsSchema
} from "./chunk-JETULU6Z.js";
import {
  ThemeConfigSchema
} from "./chunk-OVL4RTD7.js";
import {
  ColumnsPropsSchema,
  HeadingPropsSchema,
  ImagePropsSchema,
  ListPropsSchema,
  ParagraphPropsSchema,
  SectionPropsSchema,
  StatisticPropsSchema,
  TablePropsSchema
} from "./chunk-W6KB2SMK.js";
import {
  CustomComponentDefinitionSchema
} from "./chunk-7GGTZ4TA.js";

// src/validation/unified/index.ts
import {
  transformValueError,
  transformValueErrors,
  calculatePosition,
  formatErrorSummary,
  groupErrorsByPath,
  createJsonParseError
} from "@json-to-office/shared";
import {
  isUnionSchema,
  isObjectSchema,
  isLiteralSchema,
  getObjectSchemaPropertyNames,
  getLiteralValue,
  extractStandardComponentNames,
  clearComponentNamesCache,
  getSchemaMetadata
} from "@json-to-office/shared";
import {
  DEFAULT_ERROR_CONFIG as DEFAULT_ERROR_CONFIG2,
  ERROR_EMOJIS,
  createErrorConfig,
  formatErrorMessage
} from "@json-to-office/shared";

// src/validation/unified/error-formatter-config.ts
function getDefaultConfig() {
  return {
    includeEmojis: !isCI(),
    verbosity: "normal",
    includeSuggestions: true,
    includePath: true,
    maxMessageLength: 0,
    includeDocLinks: false,
    colorSupport: hasColorSupport()
  };
}
var DEFAULT_ERROR_CONFIG = getDefaultConfig();
var ERROR_TEMPLATES = {
  UNION_NO_MATCH: "Value doesn't match any of the expected formats",
  TYPE_MISMATCH: "Expected {expected} but got {actual}",
  MISSING_REQUIRED: "Missing required field '{field}'",
  UNKNOWN_PROPERTY: "Unknown property '{property}'",
  INVALID_FORMAT: "Invalid {type} format",
  PATTERN_MISMATCH: "Value doesn't match the required pattern"
};
function isCI() {
  if (typeof process === "undefined" || !process.env) {
    return false;
  }
  return !!(process.env.CI || process.env.CONTINUOUS_INTEGRATION || process.env.GITHUB_ACTIONS || process.env.GITLAB_CI || process.env.CIRCLECI || process.env.TRAVIS || process.env.JENKINS_URL);
}
function hasColorSupport() {
  if (typeof process === "undefined") {
    return false;
  }
  if (process.env?.NO_COLOR) {
    return false;
  }
  if (process.env?.FORCE_COLOR) {
    return true;
  }
  if (process.stdout?.isTTY) {
    return true;
  }
  return !isCI();
}

// src/validation/unified/theme-validator.ts
function validateTheme(data, options) {
  if (typeof data === "string") {
    return {
      valid: true,
      themeName: data
    };
  }
  const result = validateAgainstSchema(ThemeConfigSchema, data, options);
  const themeResult = {
    ...result
  };
  if (result.valid && result.data) {
    const theme = result.data;
    if (theme.name) {
      themeResult.themeName = theme.name;
    }
  }
  return themeResult;
}
function validateThemeJson(jsonInput, options) {
  const result = validateJson(ThemeConfigSchema, jsonInput, options);
  const themeResult = {
    ...result
  };
  if (result.valid && result.data) {
    const theme = result.data;
    if (typeof theme === "object" && !theme.$schema) {
      theme.$schema = "./json-schemas/theme.schema.json";
    }
    if (theme.name) {
      themeResult.themeName = theme.name;
    }
  }
  return themeResult;
}
function validateThemeWithEnhancement(data, options) {
  const result = validateTheme(data, options);
  if (!result.valid && result.errors) {
    result.errors = result.errors.map((error) => {
      if (error.path.includes("colors") && !error.suggestion) {
        error.suggestion = "Colors must be valid hex, rgb, or named colors";
      }
      if (error.path.includes("fonts") && !error.suggestion) {
        error.suggestion = "Fonts must include size and family properties";
      }
      if (error.path.includes("headings") && !error.suggestion) {
        error.suggestion = "Headings must be an array of 6 style definitions";
      }
      return error;
    });
  }
  return result;
}
function isValidTheme(result) {
  return result.valid === true && result.data !== void 0;
}
function createThemeValidator(defaultOptions) {
  return {
    validate: (data, options) => validateTheme(data, { ...defaultOptions, ...options }),
    validateJson: (jsonInput, options) => validateThemeJson(jsonInput, { ...defaultOptions, ...options }),
    validateWithEnhancement: (data, options) => validateThemeWithEnhancement(data, { ...defaultOptions, ...options })
  };
}
var themeValidator = createThemeValidator({
  clean: true,
  applyDefaults: true
});
var strictThemeValidator = createThemeValidator({
  clean: false,
  applyDefaults: false,
  maxErrors: 10
});
function getThemeName(data) {
  if (typeof data === "string") {
    return data;
  }
  if (typeof data === "object" && data !== null && "name" in data) {
    const theme = data;
    if (typeof theme.name === "string") {
      return theme.name;
    }
  }
  return void 0;
}
function isThemeConfig(data) {
  if (!data || typeof data !== "object") {
    return false;
  }
  const theme = data;
  return !!(theme.colors && theme.fonts);
}

// src/validation/unified/component-validator.ts
import { Value } from "@sinclair/typebox/value";
import { validateCustomComponentProps as sharedValidateCustomComponentProps } from "@json-to-office/shared/plugin";
var COMPONENT_SCHEMA_MAP = {
  report: ReportPropsSchema,
  section: SectionPropsSchema,
  heading: HeadingPropsSchema,
  paragraph: ParagraphPropsSchema,
  columns: ColumnsPropsSchema,
  image: ImagePropsSchema,
  statistic: StatisticPropsSchema,
  table: TablePropsSchema,
  list: ListPropsSchema,
  custom: CustomComponentDefinitionSchema
};
function validateComponent(name, props, options) {
  const schema = COMPONENT_SCHEMA_MAP[name];
  if (!schema) {
    const customResult = validateAgainstSchema(
      CustomComponentDefinitionSchema,
      props,
      options
    );
    return {
      ...customResult,
      success: customResult.valid,
      // Add success for backward compatibility
      componentName: name,
      isCustomComponent: true
    };
  }
  const result = validateAgainstSchema(schema, props, options);
  return {
    ...result,
    success: result.valid,
    // Add success for backward compatibility
    componentName: name,
    isCustomComponent: name === "custom"
  };
}
function validateComponentDefinition(component, options) {
  const result = validateAgainstSchema(
    ComponentDefinitionSchema,
    component,
    options
  );
  const componentResult = {
    ...result
  };
  if (result.valid && result.data) {
    const comp = result.data;
    componentResult.componentName = comp.name;
    componentResult.isCustomComponent = !isStandardComponentName(comp.name);
  }
  return componentResult;
}
function validateCustomComponentProps(componentSchema, config, options) {
  return sharedValidateCustomComponentProps(
    componentSchema,
    config,
    options
  );
}
function validateComponents(components, options) {
  return components.map(({ name, props }) => {
    if (isStandardComponentName(name)) {
      return validateComponent(name, props, options);
    }
    return validateComponent("custom", props, options);
  });
}
function isStandardComponentName(name) {
  return name in COMPONENT_SCHEMA_MAP;
}
function isReportProps(config) {
  return Value.Check(ReportPropsSchema, config);
}
function isSectionProps(config) {
  return Value.Check(SectionPropsSchema, config);
}
function isHeadingProps(config) {
  return Value.Check(HeadingPropsSchema, config);
}
function isParagraphProps(config) {
  return Value.Check(ParagraphPropsSchema, config);
}
function isColumnsProps(config) {
  return Value.Check(ColumnsPropsSchema, config);
}
function isImageProps(config) {
  return Value.Check(ImagePropsSchema, config);
}
function isStatisticProps(config) {
  return Value.Check(StatisticPropsSchema, config);
}
function isTableProps(config) {
  return Value.Check(TablePropsSchema, config);
}
function isListProps(config) {
  return Value.Check(ListPropsSchema, config);
}
function isCustomComponentProps(config) {
  return Value.Check(CustomComponentDefinitionSchema, config);
}
function createComponentValidator(defaultOptions) {
  return {
    validate: (name, props, options) => {
      if (isStandardComponentName(name)) {
        return validateComponent(name, props, {
          ...defaultOptions,
          ...options
        });
      }
      return validateComponent("custom", props, {
        ...defaultOptions,
        ...options
      });
    },
    validateDefinition: (component, options) => validateComponentDefinition(component, { ...defaultOptions, ...options }),
    validateCustom: (schema, config, options) => validateCustomComponentProps(schema, config, {
      ...defaultOptions,
      ...options
    })
  };
}
var componentValidator = createComponentValidator({
  clean: true,
  applyDefaults: true
});
var strictComponentValidator = createComponentValidator({
  clean: false,
  applyDefaults: false,
  maxErrors: 10
});

// src/validation/unified/index.ts
var validate = {
  // Document validation
  document: (data) => documentValidator.validate(data),
  jsonDocument: (jsonInput) => documentValidator.validateJson(jsonInput),
  // Theme validation
  theme: (data) => themeValidator.validate(data),
  jsonTheme: (jsonInput) => themeValidator.validateJson(jsonInput),
  // Component validation
  component: (name, props) => componentValidator.validate(name, props),
  componentDefinition: (component) => componentValidator.validateDefinition(component),
  // Batch operations
  components: (components) => components.map((c) => componentValidator.validate(c.name, c.props)),
  // Type checking (non-throwing)
  isDocument: (data) => {
    const result = documentValidator.validate(data);
    return result.valid;
  },
  isTheme: (data) => {
    const result = themeValidator.validate(data);
    return result.valid;
  },
  isComponent: (name, props) => {
    const result = componentValidator.validate(name, props);
    return result.valid;
  }
};
var validateStrict = {
  document: (data) => strictDocumentValidator.validate(data),
  jsonDocument: (jsonInput) => strictDocumentValidator.validateJson(jsonInput),
  theme: (data) => strictThemeValidator.validate(data),
  jsonTheme: (jsonInput) => strictThemeValidator.validateJson(jsonInput),
  component: (name, props) => strictComponentValidator.validate(name, props),
  componentDefinition: (component) => strictComponentValidator.validateDefinition(component)
};

export {
  validateTheme,
  validateThemeJson,
  validateThemeWithEnhancement,
  isValidTheme,
  createThemeValidator,
  themeValidator,
  strictThemeValidator,
  getThemeName,
  isThemeConfig,
  validateComponent,
  validateComponentDefinition,
  validateCustomComponentProps,
  validateComponents,
  isStandardComponentName,
  isReportProps,
  isSectionProps,
  isHeadingProps,
  isParagraphProps,
  isColumnsProps,
  isImageProps,
  isStatisticProps,
  isTableProps,
  isListProps,
  isCustomComponentProps,
  createComponentValidator,
  componentValidator,
  strictComponentValidator,
  ERROR_TEMPLATES,
  validate,
  validateStrict,
  transformValueError,
  transformValueErrors,
  calculatePosition,
  formatErrorSummary,
  groupErrorsByPath,
  createJsonParseError,
  isUnionSchema,
  isObjectSchema,
  isLiteralSchema,
  getObjectSchemaPropertyNames,
  getLiteralValue,
  extractStandardComponentNames,
  clearComponentNamesCache,
  getSchemaMetadata,
  DEFAULT_ERROR_CONFIG2 as DEFAULT_ERROR_CONFIG,
  ERROR_EMOJIS,
  createErrorConfig,
  formatErrorMessage
};
//# sourceMappingURL=chunk-PMTFSFFW.js.map